/**
 * Conexao WebSocket com o servidor de partida. Nao sabe NADA de jogo: conecta,
 * autentica, entrega mensagem decodificada e reconecta quando cai.
 *
 * CONTRATO (lote C e dono; lote D so chama connect/disconnect):
 *   const net = new NetClient(ctx)
 *   await net.init()
 *   net.connect(token)            conecta e manda `hello`
 *   net.disconnect(reason)        fecha sem reconectar
 *   net.send(msg)                 objeto ja pronto (use os construtores do protocolo)
 *   net.on(tipo, fn)              -> funcao que remove o ouvinte
 *   net.state                     'offline'|'connecting'|'online'|'error'
 *   net.ping                      number (ms, medido pelo ping/pong do servidor)
 *   net.playerId                  id do jogador nesta conexao | null
 *   net.update(dt)                chamado pelo Engine
 *   net.dispose()
 *
 * Reconexao com backoff exponencial (NET.RECONNECT_*). Enquanto offline, send()
 * DESCARTA em vez de enfileirar: estado de jogador velho chegando 4 s depois e
 * pior que buraco - faz o boneco voltar no tempo na tela dos outros.
 * Emite EVENTS.NET_STATUS a cada troca de estado.
 */

import { EVENTS } from '../core/EventBus.js';
import { NET } from '../../shared/netconfig.js';
import { S2C, C2S, encode, decode, hello, pong } from '../../shared/protocol.js';

/**
 * Caminho do WebSocket. Mesma origem do site: o proprio servidor Node serve o
 * `dist/`, o `/api` e o `/ws`, entao nao existe host separado para configurar.
 */
const WS_PATH = '/ws';

/** readyState do WebSocket (nao usar o numero solto pelo codigo). */
const WS_CONNECTING = 0;
const WS_OPEN = 1;

/** Fechamento normal, iniciado por nos. Qualquer outro codigo e queda. */
const CLOSE_NORMAL = 1000;

/** Fechamento que NOS provocamos por silencio do servidor (>= 4000 e livre). */
const CLOSE_TIMEOUT = 4000;

/** Peso da amostra nova no filtro do rtt. Media movel, para o numero na tela
 *  nao pular a cada pacote atrasado. */
const PING_SMOOTH = 0.3;

/** Limite superior do rtt aceito como amostra: acima disso e a aba tendo voltado
 *  do background, nao latencia de rede. */
const PING_SANE_MAX = 4000;

function nowMs() {
  return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
}

/** Fechamento enviado pelo servidor quando a conta abre outra sessao. */
const CLOSE_REPLACED = 4002;

export class NetClient {
  constructor(ctx) {
    this.ctx = ctx;

    // A rede nao para de existir porque o jogo esta pausado no menu: sem isto o
    // Engine deixaria de chamar update() e a reconexao nunca aconteceria.
    this.updateWhilePaused = true;

    // --- estado publico ---
    /** @type {'offline'|'connecting'|'online'|'error'} */
    this.state = 'offline';
    this.ping = 0;
    /** @type {number|null} */
    this.playerId = null;
    this.url = '';
    this.statusMessage = '';
    /** @type {{code:string,message:string}|null} ultimo erro vindo do servidor */
    this.lastError = null;

    /** Contadores de diagnostico (o DebugApi/HUD podem exibir; ninguem depende). */
    this.stats = { sent: 0, received: 0, dropped: 0, invalid: 0, reconnects: 0 };

    // --- interno ---
    this._ws = null;
    this._token = '';
    /** @type {Map<string, Set<Function>>} */
    this._listeners = new Map();

    // Comeca como "saida intencional": antes de alguem chamar connect(), nao
    // existe conexao para reconquistar.
    this._intentional = true;
    this._attempt = 0;          // tentativas de reconexao ja gastas
    this._retryIn = -1;         // segundos para a proxima tentativa (-1 = nenhuma)
    this._sinceRecv = 0;        // segundos desde a ultima mensagem recebida
    this._helloAt = -1;         // relogio local do envio do hello (amostra de rtt)
    this._joinAt = -1;          // idem para o join
    this._disposed = false;
    this._warned = new Set();

    // Handlers guardados para poder desligar do socket antigo sem que ele
    // continue mandando eventos (socket morto reabrindo conexao e o classico
    // "duas conexoes para a mesma sala").
    this._hOpen = () => this._onOpen();
    this._hMessage = (ev) => this._onMessage(ev);
    this._hClose = (ev) => this._onClose(ev);
    this._hError = () => this._onSocketError();
  }

  async init() {
    // Nada pesado: a conexao so nasce quando a partida pede (connect).
    if (typeof WebSocket === 'undefined') {
      this._warnOnce('websocket', new Error('WebSocket indisponivel neste ambiente'));
    }
  }

  // =========================================================================
  // API publica
  // =========================================================================

  get online() { return this.state === 'online'; }

  /**
   * Conecta e autentica. O token vai na QUERY STRING porque o WebSocket do
   * navegador nao permite mandar header Authorization - e a unica forma de
   * autenticar o handshake. O `hello` e mandado em seguida para o servidor que
   * preferir validar por mensagem.
   */
  connect(token) {
    if (this._disposed) return false;
    const t = String(token || '');
    if (!t) {
      this._fail('sem token de sessao');
      return false;
    }

    // Ja conectado com o MESMO token: nao derruba a conexao boa.
    if (t === this._token && (this.state === 'online' || (this.state === 'connecting' && this._ws))) {
      return true;
    }

    // Token novo (troca de conta): a conexao antiga nao serve mais.
    if (this._ws) this._closeSocket(CLOSE_NORMAL, 'reconectando');

    this._token = t;
    this._intentional = false;
    this._attempt = 0;
    this._retryIn = -1;
    this.lastError = null;
    return this._openSocket();
  }

  /**
   * Fecha e DESLIGA a reconexao. Sem esse desligamento o cliente brigaria para
   * voltar a uma sala que o jogador acabou de sair de proposito.
   */
  disconnect(reason = 'desconectado') {
    this._intentional = true;
    this._retryIn = -1;
    this._attempt = 0;
    this._helloAt = -1;
    this._joinAt = -1;
    this._closeSocket(CLOSE_NORMAL, reason);
    this.playerId = null;
    this._setState('offline', reason);
    return true;
  }

  /**
   * Envia uma mensagem JA construida pelos construtores do protocolo.
   *
   * Offline: DESCARTA (e conta em stats.dropped) em vez de enfileirar. Fila
   * aqui seria pior que buraco - o estado do jogador guardado enquanto a rede
   * estava fora chegaria segundos depois e faria o boneco voltar no tempo na
   * tela dos outros, alem de o servidor recusar o salto de posicao.
   */
  send(msg) {
    if (!msg || typeof msg !== 'object') return false;
    const ws = this._ws;
    if (this.state !== 'online' || !ws || ws.readyState !== WS_OPEN) {
      this.stats.dropped++;
      return false;
    }
    // Marca a saida dos dois unicos pares pergunta/resposta que o CLIENTE
    // inicia - e deles que sai a medicao honesta de ida-e-volta (ver _sampleRtt).
    if (msg.t === C2S.HELLO) this._helloAt = nowMs();
    else if (msg.t === C2S.JOIN) this._joinAt = nowMs();

    try {
      ws.send(encode(msg));
      this.stats.sent++;
      return true;
    } catch (err) {
      this.stats.dropped++;
      this._warnOnce('send', err);
      return false;
    }
  }

  /**
   * Escuta um tipo de mensagem do servidor (use as constantes S2C). O tipo '*'
   * recebe TODAS as mensagens.
   * @returns {() => void} funcao que remove o ouvinte
   */
  on(type, fn) {
    if (typeof fn !== 'function' || !type) return () => {};
    const key = String(type);
    let set = this._listeners.get(key);
    if (!set) {
      set = new Set();
      this._listeners.set(key, set);
    }
    set.add(fn);
    return () => this.off(key, fn);
  }

  off(type, fn) {
    const set = this._listeners.get(String(type));
    if (set) set.delete(fn);
  }

  /**
   * Latencia medida PELO SERVIDOR (ele manda o ping, ele cronometra o pong) e
   * devolvida no placar. Quem le o placar repassa para ca, porque o protocolo
   * nao tem ping de cliente e inventar numero na tela e pior que nao ter.
   */
  setPing(ms) {
    const v = Number(ms);
    if (!Number.isFinite(v) || v < 0 || v > PING_SANE_MAX) return;
    this.ping = this.ping > 0 ? this.ping + (v - this.ping) * PING_SMOOTH : v;
  }

  // =========================================================================
  // laco
  // =========================================================================

  update(dt) {
    if (this._disposed) return;
    try {
      const step = Number.isFinite(dt) && dt > 0 ? dt : 0;

      // Espera do backoff. Roda sem socket aberto: e justamente o intervalo
      // entre a queda e a proxima tentativa.
      if (this._retryIn > 0) {
        this._retryIn -= step;
        if (this._retryIn <= 0) {
          this._retryIn = -1;
          this._openSocket();
        }
        return;
      }

      // Vigia do silencio: o servidor manda ping a cada NET.PING_INTERVAL, logo
      // silencio por NET.CONNECTION_TIMEOUT e link morto (comum em wifi que
      // troca de ponto: o socket fica "aberto" e nada mais trafega).
      if (this._ws) {
        this._sinceRecv += step;
        if (this._sinceRecv > NET.CONNECTION_TIMEOUT) {
          this._sinceRecv = 0;
          this._closeSocket(CLOSE_TIMEOUT, 'sem resposta');
          this._scheduleReconnect('servidor sem resposta');
        }
      }
    } catch (err) {
      this._warnOnce('update', err);
    }
  }

  dispose() {
    this._disposed = true;
    this._intentional = true;
    this._retryIn = -1;
    this._closeSocket(CLOSE_NORMAL, 'encerrando');
    this._listeners.clear();
    this.playerId = null;
    this.state = 'offline';
  }

  // =========================================================================
  // socket
  // =========================================================================

  _buildUrl(token) {
    const loc = (typeof location !== 'undefined') ? location : null;
    const secure = !!loc && loc.protocol === 'https:';
    const host = (loc && loc.host) ? loc.host : 'localhost';
    const query = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${secure ? 'wss:' : 'ws:'}//${host}${WS_PATH}${query}`;
  }

  _openSocket() {
    if (this._disposed) return false;
    if (typeof WebSocket === 'undefined') {
      this._fail('WebSocket indisponivel neste navegador');
      return false;
    }
    if (this._ws) return true;

    this.url = this._buildUrl(this._token);
    this._sinceRecv = 0;
    this._setState('connecting', this._attempt > 0
      ? `reconectando (${this._attempt}/${NET.RECONNECT_TRIES})`
      : 'conectando');

    let ws = null;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      this._warnOnce('open', err);
      this._scheduleReconnect('falha ao abrir a conexao');
      return false;
    }

    this._ws = ws;
    ws.onopen = this._hOpen;
    ws.onmessage = this._hMessage;
    ws.onclose = this._hClose;
    ws.onerror = this._hError;
    return true;
  }

  /** Desliga os handlers ANTES de fechar: socket morto nao dispara mais nada. */
  _closeSocket(code, reason) {
    const ws = this._ws;
    this._ws = null;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      if (ws.readyState === WS_OPEN || ws.readyState === WS_CONNECTING) {
        // O motivo vai no fio e o limite do protocolo e 123 bytes.
        ws.close(code, String(reason || '').slice(0, 100));
      }
    } catch (err) {
      this._warnOnce('close', err);
    }
  }

  _onOpen() {
    this._sinceRecv = 0;

    // O `hello` precisa sair mesmo antes de o estado virar 'online' (send()
    // descarta fora de 'online'), por isso escreve direto no socket.
    const ws = this._ws;
    if (ws && ws.readyState === WS_OPEN) {
      try {
        this._helloAt = nowMs();
        ws.send(encode(hello(this._token)));
        this.stats.sent++;
      } catch (err) {
        this._warnOnce('hello', err);
      }
    }

    // ATENCAO: NAO virar 'online' aqui. Socket aberto nao e conexao pronta.
    // A autenticacao do servidor e ASSINCRONA (verifica o token e busca o
    // usuario no banco); ate ela terminar, qualquer mensagem nossa e recusada
    // com NOT_AUTHENTICATED e DESCARTADA. Marcando 'online' no open, o
    // MatchState mandava `join` na hora, o servidor o recusava e o jogador
    // ficava preso em "conectando" ate estourar o tempo - sem sala e sem erro
    // visivel. Quem confirma que a conexao esta usavel e o `welcome`.
  }

  _onMessage(ev) {
    this._sinceRecv = 0;
    const msg = decode(ev && ev.data);
    if (!msg) {
      this.stats.invalid++;
      return;
    }
    this.stats.received++;

    // Chegou resposta: a tentativa deu certo, o backoff volta ao inicio.
    this._attempt = 0;

    switch (msg.t) {
      case S2C.PING:
        // Responder na hora e obrigatorio: e assim que o servidor mede o rtt e
        // sabe que a conexao esta viva (NET.CONNECTION_TIMEOUT).
        this.send(pong(msg.id | 0));
        break;

      case S2C.WELCOME:
        this.playerId = (msg.you && Number.isFinite(msg.you.id)) ? (msg.you.id | 0) : this.playerId;
        this._sampleRtt(this._helloAt);
        this._helloAt = -1;
        // AQUI, e so aqui, a conexao passa a valer: o servidor so manda
        // `welcome` depois de autenticar. Antes disso o send() descarta - que e
        // justamente o que impede o `join` prematuro.
        if (this.state !== 'online') this._setState('online', 'conectado');
        break;

      case S2C.JOINED:
        if (Number.isFinite(msg.you)) this.playerId = msg.you | 0;
        this._sampleRtt(this._joinAt);
        this._joinAt = -1;
        break;

      case S2C.ERROR: {
        const code = String(msg.code || 'server_error');
        const message = String(msg.message || '');
        this.lastError = { code, message };
        this._emit(EVENTS.NET_ERROR, { code, message });
        break;
      }

      default:
        break;
    }

    this._dispatch(msg);
  }

  _onSocketError() {
    // O evento `error` do WebSocket nao traz motivo utilizavel (por seguranca do
    // navegador). Quem decide reconexao e o `close`, que vem sempre depois.
    this._warnOnce('socket', new Error('erro de socket'));
  }

  _onClose(ev) {
    this._ws = null;
    this.playerId = null;

    // 4002 = o servidor derrubou esta conexao porque a MESMA conta abriu outra
    // sessao. Reconectar aqui poe duas abas em guerra: cada reconexao derruba a
    // outra, para sempre, com uma consulta ao banco por ciclo.
    if (ev?.code === CLOSE_REPLACED) {
      this._intentional = true;
      this._setState('offline', 'Sua conta entrou em outro dispositivo.');
      return;
    }

    if (this._intentional) {
      this._setState('offline', 'desconectado');
      return;
    }
    const code = ev && Number.isFinite(ev.code) ? ev.code : 0;
    const why = (ev && ev.reason) ? String(ev.reason) : (code ? `conexao encerrada (${code})` : 'conexao encerrada');
    this._scheduleReconnect(why);
  }

  /**
   * Backoff exponencial: cada tentativa espera o dobro da anterior, com teto em
   * NET.RECONNECT_MAX_DELAY. Depois de NET.RECONNECT_TRIES para de tentar e vai
   * para 'error' - ficar batendo na porta para sempre so esconde do jogador que
   * a partida acabou.
   */
  _scheduleReconnect(reason) {
    if (this._disposed || this._intentional) return;

    if (this._attempt >= NET.RECONNECT_TRIES) {
      this._fail(`sem conexao com o servidor: ${reason}`);
      return;
    }
    this._attempt++;
    this.stats.reconnects++;
    const delay = Math.min(
      NET.RECONNECT_MAX_DELAY,
      NET.RECONNECT_BASE_DELAY * Math.pow(2, this._attempt - 1)
    );
    this._retryIn = delay;
    this._setState('connecting', `${reason} - nova tentativa em ${delay.toFixed(1)}s (${this._attempt}/${NET.RECONNECT_TRIES})`);
  }

  _fail(message) {
    this._retryIn = -1;
    this._closeSocket(CLOSE_NORMAL, 'falha');
    this.playerId = null;
    this._setState('error', message);
  }

  // =========================================================================
  // rtt
  // =========================================================================

  /**
   * Amostra de ida-e-volta REAL, tirada dos unicos pares pergunta/resposta que
   * o cliente inicia (hello->welcome, join->joined). O ping/pong do protocolo e
   * cronometrado pelo SERVIDOR - ele pergunta, nos respondemos -, entao dele nao
   * sai medicao aqui; o valor do servidor entra por setPing().
   */
  _sampleRtt(sentAt) {
    if (!(sentAt > 0)) return;
    this.setPing(nowMs() - sentAt);
  }

  // =========================================================================
  // eventos
  // =========================================================================

  _dispatch(msg) {
    this._callAll(this._listeners.get(msg.t), msg);
    this._callAll(this._listeners.get('*'), msg);
  }

  _callAll(set, msg) {
    if (!set || set.size === 0) return;
    for (const fn of Array.from(set)) {
      try {
        fn(msg);
      } catch (err) {
        this._warnOnce(`ouvinte:${msg.t}`, err);
      }
    }
  }

  _setState(next, message) {
    const msg = message === undefined ? this.statusMessage : String(message);
    if (this.state === next && this.statusMessage === msg) return;
    this.state = next;
    this.statusMessage = msg;
    // Troca de estado e evento raro (nao por frame): objeto novo e seguro aqui,
    // e evita que a UI guarde uma referencia que muda sozinha depois.
    this._emit(EVENTS.NET_STATUS, { state: next, message: msg });
  }

  _emit(type, payload) {
    try {
      this.ctx.events?.emit?.(type, payload);
    } catch (err) {
      this._warnOnce('emit', err);
    }
  }

  _warnOnce(key, err) {
    if (this._warned.has(key)) return;
    this._warned.add(key);
    console.warn(`[NetClient] falha em "${key}" - seguindo sem isso:`, err);
  }
}
